/**
 * The states every screen shares, rendered once.
 *
 * V1 section 9 requires loading, empty, no-results, unauthorized, offline,
 * expired-session, stale-edit and publishing-failed states, and requires that a
 * recoverable failure never destroys unsaved text or implies a save succeeded.
 * Those are shared behaviours, so they are shared renderers rather than nine
 * near-copies.
 *
 * Three rules hold in this file:
 *
 *   - An empty state names the actual situation and the actual next step. No
 *     centred glyph and one sentence.
 *   - An error shows the server's own words plus the request id, because a
 *     request id is the only thing an operator can use to find the log line.
 *   - Status is text plus glyph plus colour. Colour alone is never the carrier.
 */

import type { Failure } from '../api/client.js';
import { type RawHtml, attr, cls, escapeHtml, html, raw } from '../lib/html.js';

export interface FailurePresentation {
  readonly title: string;
  readonly body: string;
  readonly action: string | null;
  readonly tone: 'attention' | 'danger';
}

/**
 * The one place a failure becomes English.
 *
 * `detail` is the server's unstable explanation and is preferred when present,
 * because it is specific ("This member still owns resources that must be
 * transferred or retired first") where a generic sentence would not be. The
 * generic sentence is the fallback so a screen never renders an empty error.
 */
export function presentFailure(failure: Failure): FailurePresentation {
  switch (failure.kind) {
    case 'offline':
      return {
        title: 'No connection to the server',
        body: 'The request did not reach Skills Manager. Nothing was changed, and your text is still here. Retry when the connection is back.',
        action: 'Retry',
        tone: 'attention',
      };
    case 'not-configured':
      return {
        title: 'This deployment is not wired up',
        body: `Nothing can be read because ${failure.what} is not configured. Set the corresponding environment variable and restart the web process. Nothing here is invented to cover the gap.`,
        action: null,
        tone: 'attention',
      };
    case 'unavailable':
      return {
        title: 'Not available in this deployment',
        body: `Skills Manager has no route for ${failure.what}, and does not write the row directly to work around that. Nothing was changed.`,
        action: null,
        tone: 'attention',
      };
    case 'unauthenticated':
      return {
        title: 'Your session has ended',
        body: 'Skills Manager could not accept this session. Sign in again to continue; you will come back to this screen.',
        action: 'Sign in',
        tone: 'attention',
      };
    case 'forbidden':
      return {
        title: 'You do not have permission for this',
        body:
          failure.reason === 'browser_session_required'
            ? 'This action is reserved for a signed-in browser session and is never available to an OAuth client.'
            : 'Your membership does not carry the permission this screen needs. An organization administrator can change it.',
        action: null,
        tone: 'attention',
      };
    // NO `failure.reason` here, and this is the one failure kind in this switch
    // that does not render its own code. Every other branch prefers `detail`,
    // so the omission reads as an oversight unless the reason is written here.
    //
    // The collapse this protects was made in `apps/api`: `errors.ts` gives
    // every 404 resource code the identical `Not found.`, and
    // `apps/api/test/messages.test.ts` asserts the equality. This is the other
    // end of it. `reason` on a `not-found` is the resource code, and for an
    // expired invitation that is `invite_expired` — rendering it re-expands
    // what the service deliberately closed, and tells the reader that this token
    // belonged to an invitation that existed and lapsed rather than one that
    // never existed. That is V1 §4 rule 5's "hidden resource IDs return
    // indistinguishable 'not found' results", with a human reader instead of a
    // client. `readFailure` has already dropped `detail`, which for this code
    // carried the expiry instant itself.
    //
    // So the sentence below is the whole answer for every unusable record, and
    // the invitation screen explains expiry and resend UNCONDITIONALLY so that
    // its explanation never varies with the outcome. Changing either half
    // reopens the oracle; the two are landed together for that reason.
    case 'not-found':
      return {
        title: 'Not found',
        body: 'This record does not exist, or you are not permitted to see it. Skills Manager answers the two the same way on purpose.',
        action: null,
        tone: 'attention',
      };
    case 'conflict':
      return {
        title: 'Someone else changed this first',
        body:
          failure.detail === '' ? 'This record has changed since you loaded it.' : failure.detail,
        action: 'Reload',
        tone: 'attention',
      };
    case 'invalid':
      return {
        title: 'That request was refused',
        body: failure.detail,
        action: null,
        tone: 'attention',
      };
    case 'throttled':
      return {
        title: 'Too many requests',
        // The number is the SERVER'S, read from `Retry-After` in
        // `readFailure` (`client.ts:270-276`). It used to be a `30` written in
        // this repository's own source, which meant the countdown on screen
        // disagreed with the deadline the limiter had actually set
        // (`apps/api/src/ratelimit.ts:42-44` promises an exact one) — the one
        // status number in this application that was not the server's.
        //
        // The two branches are different sentences, not one sentence with a
        // blank in it: a missing header is an unknown deadline, and
        // substituting a plausible number for an unknown one is how a badge
        // ends up asserting something nobody measured.
        body:
          failure.retryAfterSeconds === null
            ? 'Skills Manager is rate limiting this account and did not say when the limit returns. Try again shortly.'
            : `Skills Manager is rate limiting this account. Try again in about ${failure.retryAfterSeconds} seconds.`,
        action: null,
        tone: 'attention',
      };
    case 'refused':
      return {
        title: 'The database refused this',
        body: failure.detail === '' ? failure.reason : failure.detail,
        action: null,
        tone: 'danger',
      };
    case 'server':
      return {
        title: 'The server could not complete this',
        body:
          failure.detail === '' ? 'The request failed and nothing was changed.' : failure.detail,
        action: 'Retry',
        tone: 'danger',
      };
    case 'malformed':
      return {
        title: 'The response could not be read',
        body: `Skills Manager returned something this application cannot interpret: ${failure.detail}`,
        action: 'Retry',
        tone: 'danger',
      };
  }
}

export function failurePanel(
  failure: Failure,
  options: { readonly retry: string | null } = { retry: null },
): RawHtml {
  const presented = presentFailure(failure);
  const tag = presented.tone === 'danger' ? 'Refused' : 'Unavailable';
  return html`
    <div
      class="${cls('callout', presented.tone === 'danger' ? 'callout-danger' : 'callout-attention')}"
      role=${presented.tone === 'danger' ? 'alert' : 'status'}
    >
      <span class="callout-tag">${tag}</span>
      <h2>${presented.title}</h2>
      <p>${presented.body}</p>
      ${options.retry === null ? '' : html`<button class="btn btn-secondary" data-action="${options.retry}">Retry</button>`}
      ${
        'requestId' in failure && failure.requestId
          ? html`<p class="state-meta">request ${failure.requestId}</p>`
          : ''
      }
    </div>
  `;
}

export interface EmptyState {
  readonly title: string;
  readonly body: string;
  readonly primary: { readonly label: string; readonly action: string; readonly id?: string } | null;
  readonly secondary: readonly { readonly label: string; readonly action: string }[];
}

/**
 * The `id` an empty state's primary button renders, or nothing at all.
 *
 * WHY THIS EXISTS. V1 section 9 requires modal focus return, and an empty
 * library or toolkit list is the first-run state of every new organization —
 * so the empty state is not an edge case, it is where a new user meets the
 * "new skill" button first. When the same `data-action` also appears on a
 * toolbar button, there are two copies of one control and a dialog opened from
 * the empty-state copy has no way to name the control that opened it.
 *
 *   Focus cannot be restored by `data-action` here. Both copies share it,
 *   `querySelector` returns whichever comes first in document order, and that is
 *   the toolbar button. Focus would land on a different visible control than the
 *   one the person pressed — which is worse than leaving focus on `BODY`, because
 *   it is confidently wrong rather than merely absent.
 *
 * So an id is carried, and it is OPTIONAL on purpose: with no `id` this returns
 * the empty string and the button renders byte-identically to every caller that
 * does not need one.
 */
function primaryIdAttribute(primary: NonNullable<EmptyState['primary']>): RawHtml {
  return primary.id === undefined ? raw('') : raw(` id="${escapeHtml(primary.id)}"`);
}


/**
 * An empty state, left aligned, naming the situation.
 *
 * There is no centred icon anywhere in this application. A person who has just
 * filtered to nothing needs to know which filter produced the nothing.
 */
export function emptyState(state: EmptyState): RawHtml {
  return html`
    <div class="state">
      <h2>${state.title}</h2>
      <p>${state.body}</p>
      <div class="btn-row">
        ${
          state.primary === null
            ? ''
            : html`<button class="btn btn-primary"${primaryIdAttribute(state.primary)} data-action="${state.primary.action}">${state.primary.label}</button>`
        }
        ${state.secondary.map((item) => html`<button class="btn btn-secondary" data-action="${item.action}">${item.label}</button>`)}
      </div>
    </div>
  `;
}

export function skeletonRows(columns: readonly string[], rows: number): RawHtml {
  return html`
    <div class="table-scroll" aria-busy="true" aria-live="polite">
      <span class="visually-hidden">Loading</span>
      <table class="data">
        <thead>
          <tr>
            ${columns.map((column) => html`<th scope="col">${column}</th>`)}
          </tr>
        </thead>
        <tbody>
          ${Array.from(
            { length: rows },
            () => html`
            <tr class="skeleton-row">
              ${columns.map(() => html`<td><span class="skeleton-bar"></span></td>`)}
            </tr>
          `,
          )}
        </tbody>
      </table>
    </div>
  `;
}

export type TagTone = 'plain' | 'accent' | 'attention' | 'danger';

const TAG_CLASS: Readonly<Record<TagTone, string>> = {
  plain: '',
  accent: 'tag-accent',
  attention: 'tag-attention',
  danger: 'tag-danger',
};

/** A status tag. `glyph` is part of the contract: colour is never alone. */
export function tag(label: string, glyph: string, tone: TagTone = 'plain'): RawHtml {
  return html`<span class="${cls('tag', TAG_CLASS[tone])}"><span class="glyph" aria-hidden="true">${glyph}</span>${label}</span>`;
}

export interface DocHeadEntry {
  readonly label: string;
  readonly value: string;
}

/**
 * The document header: one line of monospace metadata over a title.
 *
 * This is the reference's signature and it is on every screen. `ref` is the
 * record number, the organization slug and the revision or state, which is what
 * a person quotes in a message.
 */
export function docHead(entries: readonly DocHeadEntry[], title: string, lede?: string): RawHtml {
  const [first, ...rest] = entries;
  return html`
    <div class="doc-head">
      ${first === undefined ? '' : html`<span class="ref">${first.label} ${first.value}</span>`}
      ${rest.map((entry) => html`<span>${entry.label} ${entry.value}</span>`)}
    </div>
    <h1>${title}</h1>
    ${lede === undefined ? '' : html`<p class="lede">${lede}</p>`}
  `;
}

/**
 * A checkpoint as the document declares it.
 *
 * V1 section 9: "Show checkpoints defined in the document; do not claim they
 * were executed." So the line states where it comes from and stops there.
 */
export function checkpoint(id: string, text: string): RawHtml {
  return html`
    <div class="checkpoint">
      <span class="checkpoint-mark" aria-hidden="true"></span>
      <span>
        <span class="checkpoint-id">Human checkpoint ${id}</span>
        <span class="checkpoint-text">${text}</span>
      </span>
    </div>
  `;
}

/** A button. `variant` picks the weight; a view chooses exactly one primary. */
export function button(
  label: string,
  action: string,
  variant: 'primary' | 'secondary' | 'danger' | 'quiet' = 'secondary',
  extra: Readonly<Record<string, string>> = {},
): RawHtml {
  const attributes = Object.entries(extra)
    .map(([name, value]) => attr(name, value))
    .join('');
  // `attr` has already escaped every value, so the joined string is safe to
  // append without passing it back through the escaping template.
  // The `RawHtml` itself, not `.value`. Returning the string is the same bug the
  // other components had: a plain string goes back through the escaping template
  // when a view interpolates it, and the button's markup becomes visible text.
  return raw(
    `<button class="btn btn-${variant}" data-action="${escapeHtml(action)}"${attributes}>${escapeHtml(label)}</button>`,
  );
}
